import { LoadingState } from "@material-dev/insights-portal-ui-components/lib/types";
import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import { TextVariablesClient, ITextVariablesClient, PollingStatuses, DownloadTextVariablesCsvCommand, ApiException, ErrorResponse, ProjectDto } from "app/ApiClient";
import { initialDownloadTextVariables, TextVariablesDownloadToken } from "./downloadTextVariablesState";
import { IState } from "slices";
import { tryGetValueFromJson } from "utils/parseHelper";
const POLLING_INTERVAL_MS = 5000;
const DOWNLOAD_ERROR_MSG = "CSV generation failed.";

const textVariablesClient: ITextVariablesClient = new TextVariablesClient(
  process.env.REACT_APP_API_URL,
);

const setTextVariablesDownloadToken = (
  textVariablesDownloadTokens: TextVariablesDownloadToken[],
  token: string,
  command: DownloadTextVariablesCsvCommand,
) => {
  let textVariablesDownloadToken: TextVariablesDownloadToken | undefined = textVariablesDownloadTokens.find(
    (x) => x.command.projectId == command.projectId,
  );
  if (textVariablesDownloadToken) {
    textVariablesDownloadToken.token = token;
  } else {
    textVariablesDownloadToken = {
      token: token,
      command: command,
    };

    textVariablesDownloadTokens.push(textVariablesDownloadToken);
  }
  return textVariablesDownloadTokens;
};

const deleteTextVariablesDownloadToken = (
  textVariablesDownloadTokens: TextVariablesDownloadToken[],
  token?: string,
  projectId?: number,
) => {
  if (token) {
    return textVariablesDownloadTokens.filter((x) => x.token !== token);
  } else {
    return textVariablesDownloadTokens.filter((x) => x.command.projectId !== projectId);
  }
};

const setTextVariablesDownloadTokensAndGetTokenStatus = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  token: string,
  command: DownloadTextVariablesCsvCommand,
) => {
  setTimeout(() => {
    dispatch(
      downloadTextVariablesTokenReceived({
        token: token,
        command
      }),
    );

    dispatch(getTextVariablesDownloadStatus({ token }));
  }, POLLING_INTERVAL_MS);
};

const deleteTextVariablesDownloadTokenOnError = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  token: string,
  projectId?: number,
  currentProjectId?: number,
  errorMessage?: string,
) => {
  dispatch(
    downloadTextVariablesTokenRemoved({
      token: token,
      status: LoadingState.failed,
      projectId: projectId,
      currentProjectId: currentProjectId,
      errorMessage: errorMessage
    }),
  );
};

export const downloadTextVariablesCsvByProjectId = createAsyncThunk(
  "textVariables/downloadCsv",
  async (command: DownloadTextVariablesCsvCommand,
    { rejectWithValue, dispatch, getState }) => {
      const { project: { currentProject },
    } = getState() as IState;
    try {
      const result = await textVariablesClient.downloadCsv({...command, version:"latest"});
      if (result.url) {
        saveAs(result.url);
        dispatch(
            downloadTextVariablesTokenRemoved({
              token: "",
              status: LoadingState.succeed,
              projectId: command.projectId,
              currentProjectId: currentProject?.id,
            }),
          );
      }
      else if(result.token) {
        setTextVariablesDownloadTokensAndGetTokenStatus(dispatch, result.token, command);
      }
    } catch (error) {
      let errorMessage;
      if (ApiException.isApiException(error)) {
        const { response, message } = error;
        const parsedResponse = JSON.parse(response) as ErrorResponse;
        errorMessage = tryGetValueFromJson(parsedResponse.errorMessage ?? message, "detail");
      }
      deleteTextVariablesDownloadTokenOnError(dispatch, "", command.projectId, currentProject?.id, errorMessage);
      return rejectWithValue(error);
    }
  },
);

export const getTextVariablesDownloadStatus = createAsyncThunk(
  "textVariables/getDownloadStatus",
  async ({ token }: { token: string }, { dispatch, getState }) => {
    const {
      downloadTextVariables: { textVariablesDownloadTokens },
    } = getState() as IState;
    const {
      project: { currentProject },
    } = getState() as IState;
    const command: DownloadTextVariablesCsvCommand | undefined = textVariablesDownloadTokens?.find((x) => x.token == token)?.command;
    try {
      if (!command || !command.projectId) return;
      const result = await textVariablesClient.getDownloadStatus(token);
      switch (result?.status) {
        case PollingStatuses.Complete:
          dispatch(downloadOETextVariablesCsvByProjectId({command, token, currentProject}));
          break;
        case PollingStatuses.InProgress:
          setTextVariablesDownloadTokensAndGetTokenStatus(dispatch, token, command);
          break;
        default:
          dispatch(
            downloadTextVariablesTokenRemoved({
              token,
              status: LoadingState.failed,
              projectId: command.projectId,
              currentProjectId: currentProject?.id,
            }),
          );
          break;
      }
    } catch (error) {
      deleteTextVariablesDownloadTokenOnError(dispatch, token, command?.projectId, currentProject?.id);
    }
  },
);

export const downloadOETextVariablesCsvByProjectId = createAsyncThunk(
  "textVariables/downloadOpenEndCsv",
  async ({command, token, currentProject}: { command: DownloadTextVariablesCsvCommand, token: string, currentProject: ProjectDto | null },
    { dispatch, rejectWithValue }) => {
    try {
      const result = await textVariablesClient.downloadOpenEndCsv({...command, version:"latest"});
      dispatch(
        downloadTextVariablesTokenRemoved({
          token: token,
          status: LoadingState.succeed,
          projectId: command.projectId,
          currentProjectId: currentProject?.id,
        }),
      );
      if (result.url) {
        saveAs(result.url);
      }
    } catch (error) {
      let errorMessage;
      if (ApiException.isApiException(error)) {
        const { response, message } = error;
        const parsedResponse = JSON.parse(response) as ErrorResponse;
        errorMessage = tryGetValueFromJson(parsedResponse.errorMessage ?? message, "detail");
      }
      deleteTextVariablesDownloadTokenOnError(dispatch, token, command.projectId, currentProject?.id, errorMessage);
      return rejectWithValue(error);
    }
  },
);

const downloadTextVariablesSlice = createSlice({
  name: "downloadTextVariables",
  initialState: initialDownloadTextVariables,
  reducers: {
    resetDownloadTextVariablesStatus: (state) => {
      state.textVariablesDownloadingState = LoadingState.idle;
      state.downloadTextVariablesErrorMessage = undefined;
    },
    downloadTextVariablesTokenReceived: (
      state,
      { payload: { token, command } }: { payload: { token: string; command: DownloadTextVariablesCsvCommand } },
    ) => {
      state.textVariablesDownloadTokens = setTextVariablesDownloadToken(
        state.textVariablesDownloadTokens,
        token,
        command,
      );
    },
    downloadTextVariablesTokenRemoved: (
      state,
      {
        payload: { token, status, projectId, currentProjectId, errorMessage },
      }: {
        payload: {
          token: string;
          status: LoadingState;
          projectId?: number;
          currentProjectId?: number;
          errorMessage?: string;
        };
      },
    ) => {
      state.textVariablesDownloadTokens = deleteTextVariablesDownloadToken(
        state.textVariablesDownloadTokens,
        token,
        projectId,
      );
      if (currentProjectId && projectId === currentProjectId) {
        state.textVariablesDownloadingState = status;
        if (status == LoadingState.failed) {
          state.downloadTextVariablesErrorMessage = errorMessage ?? DOWNLOAD_ERROR_MSG;
        } else {
          state.downloadTextVariablesErrorMessage = undefined;
        }
      } else {
        state.downloadTextVariablesErrorMessage = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadTextVariablesCsvByProjectId.pending, (state, {meta}) => {
        state.textVariablesDownloadingState = LoadingState.pending;
        state.downloadTextVariablesErrorMessage = undefined;
        state.textVariablesDownloadTokens = setTextVariablesDownloadToken(
          state.textVariablesDownloadTokens,
          "",
          meta.arg,
        );
      },
    );
  },
});

export const {
  resetDownloadTextVariablesStatus,
  downloadTextVariablesTokenReceived,
  downloadTextVariablesTokenRemoved,
} = downloadTextVariablesSlice.actions;
export default downloadTextVariablesSlice.reducer;
